import { ApiError } from "./api-error.js";

/** Reads the CSRF cookie. Deliberately script-readable — that is how double-submit works. */
export type CsrfTokenReader = () => string | undefined;

export interface ApiClientOptions {
  baseUrl?: string;
  /** Returns the in-memory access token, or undefined when signed out. */
  getAccessToken: () => string | undefined;
  /** Mints a fresh access token from the refresh cookie. Returns undefined when that fails. */
  refreshAccessToken: () => Promise<string | undefined>;
  getCsrfToken: CsrfTokenReader;
  /** Called when the session is unrecoverable, so the app can route to sign-in. */
  onUnauthenticated?: () => void;
}

const CSRF_HEADER = "x-gladis-csrf";
const STATE_CHANGING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * The typed HTTP client.
 *
 * Two responsibilities that exist here so no caller has to remember them:
 *
 *  1. The CSRF header on every state-changing request.
 *  2. Silent refresh, exactly once, on a 401.
 *
 * The single-retry rule is the important half. Retrying after the refresh itself fails produces an
 * infinite loop that presents as a hung application — so a failed refresh routes to sign-in and
 * stops. See docs/architecture/frontend.md §3.
 */
export class ApiClient {
  private readonly baseUrl: string;
  /** In-flight refresh, shared so concurrent 401s trigger one refresh rather than a stampede. */
  private refreshInFlight: Promise<string | undefined> | null = null;

  constructor(private readonly options: ApiClientOptions) {
    this.baseUrl = options.baseUrl ?? "/api";
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>("GET", path);
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("POST", path, body);
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("PATCH", path, body);
  }

  /**
   * PUT — for endpoints that SET a state rather than record an event.
   *
   * Added for `PUT /api/me/active-household`: switching to the household you are already in is the
   * same state, not a second switch. PATCH would imply a partial update of a resource; POST would
   * imply an event worth repeating.
   */
  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("PUT", path, body);
  }

  /**
   * DELETE, optionally with a body.
   *
   * A body on DELETE is unusual and legal. It exists for one endpoint — deleting a household, which
   * requires the household's name typed back as confirmation (FR-023) — where the alternative
   * shapes are worse: a query string would put the confirmation in logs and browser history, and a
   * `POST /households/:id/delete` would describe the product's only irreversible action as if it
   * created something.
   *
   * Fail-safe if an intermediary ever strips it: the server requires an exact match, so a missing
   * body is a refusal rather than a deletion.
   */
  delete<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("DELETE", path, body);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    let response = await this.send(method, path, body);

    if (response.status === 401) {
      const token = await this.refreshOnce();
      if (token) {
        response = await this.send(method, path, body);
      }
      if (response.status === 401) {
        // Refresh failed, or succeeded and the retry still came back unauthorised. Either way the
        // session is unrecoverable — stop here rather than trying again.
        this.options.onUnauthenticated?.();
        throw await toApiError(response);
      }
    }

    if (!response.ok) throw await toApiError(response);
    return (await readBody<T>(response)) as T;
  }

  private send(method: string, path: string, body?: unknown): Promise<Response> {
    const headers: Record<string, string> = {};

    const token = this.options.getAccessToken();
    if (token) headers["authorization"] = `Bearer ${token}`;

    if (STATE_CHANGING.has(method)) {
      const csrf = this.options.getCsrfToken();
      if (csrf) headers[CSRF_HEADER] = csrf;
    }

    if (body !== undefined) headers["content-type"] = "application/json";

    return fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      // Same-origin, so the refresh cookie travels on the auth routes and nowhere else.
      credentials: "same-origin",
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  }

  private refreshOnce(): Promise<string | undefined> {
    this.refreshInFlight ??= this.options.refreshAccessToken().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }
}

/**
 * Reads a response body, tolerating the ones that have none.
 *
 * 204 is the obvious empty response and was the only one handled at first — which broke on the
 * first 202. An accepted-but-not-applied request (the email change) carries no body either, and
 * `response.json()` on an empty body throws a parse error that surfaces in the UI as gibberish
 * rather than as anything actionable.
 *
 * Checked by content rather than by status, so the next status that happens to be empty is already
 * handled.
 */
async function readBody<T>(response: Response): Promise<T | undefined> {
  if (response.status === 204 || response.status === 205) return undefined;

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) return undefined;

  const text = await response.text();
  return text.length === 0 ? undefined : (JSON.parse(text) as T);
}

async function toApiError(response: Response): Promise<ApiError> {
  const fallback = `Request failed with status ${response.status}`;
  const body = (await response.json().catch(() => null)) as {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  } | null;

  return new ApiError(
    body?.code ?? "SYS_INTERNAL",
    body?.message ?? fallback,
    response.status,
    body?.details,
  );
}
